import styles from "./FeatureAddEdit.module.css";
import { Button, TextField, Autocomplete, Checkbox, FormControlLabel } from "@mui/material";
import { useSnackbar } from "notistack";
import Icon from "@mdi/react";
import { mdiChevronLeft } from "@mdi/js";

// React
import React, { useState, useEffect } from "react";
import { Redirect, useParams } from "react-router-dom";

// Tools
import Services from "tools/Services";
import { dropDownModuleOptionsArray } from "tools/getModule";

//Components
import DropDownMenu from "components/DropDownMenu";
import Loader from "components/Loader";
import { dropDownPublicationOptionsArray } from "tools/getPublication";
import { dropDownStatusOptionsArray } from "tools/getStatus";
import { dropDownPriorityOptionsArray } from "tools/getPriority";

export default function PageFeatureAddEdit() {
	const [users, setUsers] = useState([]);
	const [feature, setFeature] = useState({
		title: "",
		description: "",
		estDevTimeInDays: 0,
		idFeedback: -1,
		custom: false,
		beta: false,
		status: 1,
		priority: 0,
		published: false,
		requestedBy: [],
	});
	const [redirect, setRedirect] = useState(false);
	const [loading, setLoading] = useState(true);
	const { id } = useParams();
	const { enqueueSnackbar } = useSnackbar();
	const [error, setError] = useState(false);

	useEffect(() => {
		loadUsers();
		if (id !== "new") loadFeature(id);
		else setLoading(false);
	}, [id]);

	// fetch data
	const loadFeature = async (id) => {
		// GET
		let res = await Services.get("/Feature/getFeature/" + id);

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		// SUCCESS
		setFeature(res.data);
		setLoading(false);
	};

	const loadUsers = async () => {
		// GET
		let res = await Services.get("/User/getUsers/");

		// ERROR
		if (res.status !== 200) {
			console.error(res);
			return;
		}

		// SUCCESS
		// We only can choose Users not admins to add requested by
		setUsers(res.data.filter((user) => user.roles == "User"));
	};

	// handles
	const handleClickSave = async (e) => {
		e.preventDefault();

		if (feature.title === "" || feature.description === "") {
			setError(true);
			return;
		}
		let updatedFeature = feature;

		// PUT
		let res = await Services.put("/Feature", updatedFeature);

		// Error
		if (res.status !== 200) {
			console.error(res);
			enqueueSnackbar(res.error, {
				variant: "error",
			});
			return;
		}

		// SUCCESS
		setRedirect(true);
		enqueueSnackbar("Changes saved", {
			variant: "success",
		});
	};

	//handles
	const handleChange = (e) => {
		let value = e.target.value;
		let name = e.target.name;
		setFeature({ ...feature, [name]: value });
		setError(false);
	};

	const handleItemSelected = (item, type) => {
		let value = item.id;
		if (type == "published") value = !!value;
		setFeature({ ...feature, [type]: value });
	};

	const handleChangeRequestedBy = (e, values) => {
		setFeature({ ...feature, requestedBy: values });
	};

	const handleChangeCheckbox = (e) => {
		setFeature({ ...feature, [e.target.name]: e.target.checked });
	};

	return (
		<div className={styles.container}>
			{redirect && <Redirect to="/Features" />}

			<Button variant="outlined" className={styles.button} onClick={() => setRedirect(true)}>
				<Icon path={mdiChevronLeft} size={1} /> Back
			</Button>
			{loading ? (
				<Loader />
			) : (
				<form className={styles.form} onSubmit={handleClickSave}>
					<div className={styles.header}>
						<h2 className={styles.title}>{feature.title}</h2>{" "}
						{id === "new" ? (
							<div className={styles.adding}>Adding</div>
						) : (
							<div className={styles.editing}>Editing</div>
						)}
					</div>
					<div className={styles.module}>
						<DropDownMenu
							items={[...dropDownModuleOptionsArray]}
							selectedItem={
								id == "new" ? dropDownModuleOptionsArray[0] : dropDownModuleOptionsArray[feature.module]
							}
							width={{ width: 160 }}
							onSelect={handleItemSelected}
							selectedItemWithLabel={true}
							type="module"
						/>

						<label className={styles.label}>Estimated dev. time (in days)</label>
						<TextField
							sx={{ width: 30 }}
							variant="standard"
							name="estDevTimeInDays"
							placeholder="Nb"
							type="text"
							value={feature.estDevTimeInDays}
							onChange={handleChange}
						></TextField>
						<DropDownMenu
							items={dropDownPriorityOptionsArray}
							selectedItem={dropDownPriorityOptionsArray[feature.priority]}
							width={{ width: 135 }}
							onSelect={handleItemSelected}
							type="priority"
						/>
						<DropDownMenu
							items={dropDownPublicationOptionsArray}
							selectedItem={dropDownPublicationOptionsArray[+feature.published]}
							width={{ width: 80 }}
							onSelect={handleItemSelected}
							type="published"
						/>
						<DropDownMenu
							items={dropDownStatusOptionsArray}
							selectedItem={dropDownStatusOptionsArray[feature.status]}
							width={{ width: 160 }}
							onSelect={handleItemSelected}
							type="status"
						/>
						<label className={styles.label}>Feedback ID</label>
						<TextField
							sx={{ width: 60 }}
							variant="standard"
							name="idFeedback"
							placeholder="Nb"
							type="text"
							value={feature.idFeedback}
							onChange={handleChange}
						></TextField>
						<FormControlLabel
							label="Custom"
							control={
								<Checkbox checked={feature.custom} name="custom" onChange={handleChangeCheckbox} />
							}
						/>
						<FormControlLabel
							label="Beta"
							control={<Checkbox checked={feature.beta} name="beta" onChange={handleChangeCheckbox} />}
						/>
					</div>
					<Autocomplete
						sx={{ minWidth: 200, zIndex: 0 }}
						multiple
						id="tags-outlined"
						options={users}
						getOptionLabel={(option) => option.userName}
						value={feature.requestedBy}
						filterSelectedOptions
						isOptionEqualToValue={(option, value) => option.id === value.id}
						onChange={handleChangeRequestedBy}
						renderInput={(params) => <TextField {...params} label="Requested By" placeholder="" />}
					/>
					<label className={styles.label}>Title</label>
					<TextField
						variant="standard"
						error={error}
						name="title"
						placeholder="Add a title for the new feature"
						type="text"
						value={feature.title}
						onChange={handleChange}
					></TextField>
					<label className={styles.label}>Description</label>
					<TextField
						multiline
						error={error}
						rows={8}
						variant="standard"
						name="description"
						placeholder="Add a description of the new feature"
						type="text"
						value={feature.description}
						onChange={handleChange}
						className={styles.description}
					/>
					{error && <div className={styles.error}>Please don't leave fields empty 😢</div>}
					<div className={styles.formButtons}>
						<Button variant="outlined" onClick={() => setRedirect(true)}>
							Cancel
						</Button>
						<Button type="submit" variant="contained" onClick={handleClickSave} sx={{ color: "#FFD" }}>
							Save
						</Button>
					</div>
				</form>
			)}
		</div>
	);
}
